/**
 * "Gert á eldri útgáfu nótnanna" controls for one sheet, in view and edit mode alike: which layers
 * are stale (own / other people's, by the engine's one rule), the per-sheet "Fela lög af eldri
 * útgáfu" filter (engine.setHideStaleLayers — a filter, so confirmed layers show again by
 * themselves), and the "Skoða og staðfesta" review sheet (render `sheet` once). Confirming needs no
 * edit mode: it goes through the engine's outbox (offline too).
 */
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import type { SongFileDTO } from '../../shared/dto/library';
import type { LayerView, SheetAnnotations } from '../annotations/contract';
import { useMembers } from '../api/members';
import { useToast } from '../components/toast';
import { useT } from '../i18n';
import { useOnline } from '../lib/useOnline';
import { shareSummary } from './layerText';
import { staleLayers } from './staleLayers';
import { StaleReviewSheet } from './StaleReviewSheet';

export interface StaleControls {
  /** Own stale layers ("Skoða og staðfesta" when canConfirm and there are any). */
  own: LayerView[];
  /** Other people's stale layers (labelled only). */
  shared: LayerView[];
  ids: ReadonlySet<string>;
  /** The engine can confirm a revision. */
  canConfirm: boolean;
  /** The engine has the per-sheet hide filter. */
  canHide: boolean;
  /** "Fela lög af eldri útgáfu" is on for this sheet. */
  hidden: boolean;
  /** The panel's switch. Stable. */
  setHidden(hide: boolean): void;
  /** The banner's action: hides every stale layer (again), with "Sýna aftur" in the toast. Stable. */
  hideAll(): void;
  /** Opens the review for these own stale layers (default: all of them). Stable. */
  review(ids?: readonly string[]): void;
  /** The review sheet. */
  sheet: ReactNode;
}

export function useStaleLayers(
  engine: SheetAnnotations,
  file: Pick<SongFileDTO, 'rev'>,
  opts: {
    /** "Skoða fyrst" closes the review; also close what is around it (e.g. the layer panel). */
    onLookFirst?: () => void;
  } = {},
): StaleControls {
  const t = useT();
  const toast = useToast();
  const online = useOnline();
  const stale = useMemo(() => staleLayers(engine.layers, file.rev), [engine.layers, file.rev]);
  const [reviewIds, setReviewIds] = useState<readonly string[] | null>(null);
  const members = useMembers(reviewIds !== null);
  const byId = useMemo(() => new Map((members.data ?? []).map((m) => [m.id, m])), [members.data]);

  // The stable callbacks read the latest render's values.
  const latest = useRef({ engine, stale, t, toast });
  latest.current = { engine, stale, t, toast };

  const setHidden = useCallback((hide: boolean) => latest.current.engine.setHideStaleLayers?.(hide), []);
  const hideAll = useCallback(() => {
    const cur = latest.current;
    if (!cur.engine.setHideStaleLayers) return;
    cur.engine.setHideStaleLayers(true);
    cur.toast.show(cur.t('annotate.stale_hidden_toast'), {
      ms: 6000,
      action: { label: cur.t('annotate.stale_show_again'), onClick: () => latest.current.engine.setHideStaleLayers?.(false) },
    });
  }, []);
  const review = useCallback((ids?: readonly string[]) => setReviewIds(ids ?? latest.current.stale.own.map((l) => l.id)), []);

  const confirm = (ids: string[]) => {
    for (const id of ids) engine.confirmLayerRevision?.(id, file.rev);
    setReviewIds(null);
    if (ids.length) toast.show(online ? t('annotate.stale_confirmed', { count: ids.length }) : t('annotate.stale_confirmed_offline'), { tone: 'ok' });
  };

  const lookFirst = (ids: string[]) => {
    // Also lets them through the hide filter.
    for (const l of stale.own) if (ids.includes(l.id) && !l.visible) engine.setLayerVisible(l.id, true);
    setReviewIds(null);
    opts.onLookFirst?.();
    toast.show(t('annotate.stale_look_hint'));
  };

  const reviewLayers = reviewIds ? stale.own.filter((l) => reviewIds.includes(l.id)) : null;
  const sheet = (
    <StaleReviewSheet
      layers={reviewLayers}
      describe={(l) => shareSummary(l, t, byId)}
      onConfirm={confirm}
      onLookFirst={lookFirst}
      onClose={() => setReviewIds(null)}
    />
  );

  return {
    own: stale.own,
    shared: stale.shared,
    ids: stale.ids,
    canConfirm: typeof engine.confirmLayerRevision === 'function',
    canHide: typeof engine.setHideStaleLayers === 'function',
    hidden: !!engine.hideStaleLayers,
    setHidden,
    hideAll,
    review,
    sheet,
  };
}
